import React, { useEffect, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import logo from '../images/logo.png';
import CustomPopup from './CustomPopup';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();

  // Popup state
  const [popupConfig, setPopupConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'success',
    display: 'center'
  });

  const showPopup = (title, message, type = 'success', display = 'center') => {
    setPopupConfig({
      isOpen: true,
      title,
      message,
      type,
      display
    });
  };

  const closePopup = () => {
    setPopupConfig(prev => ({ ...prev, isOpen: false }));
  };

  // Redirect if already logged in
  useEffect(() => {
    const user = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (user && token) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://chatbot-ai-m3th.onrender.com/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }

      const { access_token, user_data } = await response.json();
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user_data));
      
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
      showPopup('Login Failed', err.message || 'Please check your credentials', 'error', 'center');
    } finally {
      setLoading(false);
    }
  };

  // 🔥 Updated: Same flow as Signup - check email first, then show appropriate popup
  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    setError('');

    try {
      // 🔥 STEP 1: Decode Google token to get email FIRST
      const token = credentialResponse.credential;
      const tokenParts = token.split('.');
      
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const userEmail = payload.email;
        const username = payload.name || (userEmail ? userEmail.split('@')[0] : 'User');
        
        // 🔥 STEP 2: Check if email exists BEFORE calling login API
        let isExistingUser = false;
        
        if (userEmail) {
          try {
            const checkEmailResponse = await fetch(`https://chatbot-ai-m3th.onrender.com/check-email?email=${encodeURIComponent(userEmail)}`);
            const checkData = await checkEmailResponse.json();
            
            isExistingUser = checkData.available === false;
            
          } catch (error) {
            console.error("❌ Failed to check email:", error);
          }
        }

        // 🔥 STEP 3: Now call the login API
        const response = await fetch('https://chatbot-ai-m3th.onrender.com/login/google', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: credentialResponse.credential }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Google login failed');
        }

        const data = await response.json();
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user_data));

        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (err) {
      console.error("❌ Google login error:", err);
      setError(err.message || 'Google login failed');
      showPopup('Google Login Failed', err.message || 'Please try again', 'error', 'center');
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.error("❌ Google sign-in error");
    setError('Google sign-in failed');
    showPopup('Error', 'Failed to sign in with Google. Please try again.', 'error', 'center');
  };

  return (
    <>
      {/* 🔥 Full Screen Google Loading Overlay */}
      {googleLoading && (
        <div style={styles.fullScreenLoadingOverlay}>
          <div style={styles.loadingCardFull}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3.5rem', height: '3.5rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={styles.loadingTextFull}>Signing in with Google...</p>
            <p style={styles.loadingSubtext}>Please wait while we log you in</p>
          </div>
        </div>
      )}

      {/* Navbar Section */}
      <nav className="navbar navbar-expand-lg navbar-dark py-3" style={{ opacity: googleLoading ? 0.5 : 1 }}>
        <div className="container">
          <button
            className="navbar-brand d-flex align-items-center gap-2 btn btn-link text-decoration-none p-0 border-0"
            onClick={() => navigate('/')}
            disabled={googleLoading}
          >
            <img src={logo} alt='Marshall AI Logo' className='logo' />
            <span className="fs-4 fw-bold text-light">Marshall AI</span>
          </button>
          <div className="d-flex gap-3">
            <button
              className="btn btn-outline-primary"
              onClick={() => navigate('/login')}
              disabled={googleLoading}
            >
              Login
            </button>
            <button
              className="btn btn-dark"
              onClick={() => navigate('/signup')}
              disabled={googleLoading}
            >
              Sign Up
            </button>
          </div>
        </div>
      </nav>

      {/* Login Form Section */}
      <div style={styles.container}>
        <div style={{ ...styles.card, position: 'relative' }}>
          
          {/* 🔥 Manual Login Loading Overlay */}
          {loading && (
            <div style={styles.loadingOverlay}>
              <div style={styles.loadingCardInline}>
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p style={styles.loadingTextInline}>Signing you in...</p>
              </div>
            </div>
          )}

          <h2 style={styles.title}>Welcome Back</h2>
          <p style={styles.subtitle}>Sign in to continue to your account</p>

          {error && <div style={styles.error}>{error}</div>}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label htmlFor="email" style={styles.label}>Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
                placeholder="Enter your email"
                required
                disabled={loading || googleLoading}
              />
            </div>

            <div style={styles.inputGroup}>
              <label htmlFor="password" style={styles.label}>Password</label>
              <div style={styles.inputWrapper}>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ ...styles.input, paddingRight: '40px' }}
                  placeholder="Enter your password"
                  required
                  disabled={loading || googleLoading}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || googleLoading}
              style={{
                ...styles.button,
                ...((loading || googleLoading) ? styles.buttonDisabled : {})
              }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div style={styles.divider}>
            <span style={styles.dividerText}>OR</span>
          </div>

          <div style={styles.socialLogin}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              size="large"
              text='signin_with'
              disabled={loading || googleLoading}
            />
          </div>

          <p style={styles.footer}>
            Don't have an account?{' '}
            <a
              href="/signup"
              style={styles.link}
              onClick={(e) => {
                e.preventDefault();
                navigate('/signup');
              }}
            >
              Sign up
            </a>
          </p>
          
          <p style={styles.forgotPassword}>
            <a
              href="/forgot-password"
              style={styles.forgotLink}
              onClick={(e) => {
                e.preventDefault();
                showPopup('Coming Soon', 'Password reset feature will be available soon!', 'info', 'center');
              }}
            >
              Forgot password?
            </a>
          </p>
        </div>
      </div>

      {/* Custom Popup */}
      <CustomPopup
        isOpen={popupConfig.isOpen}
        onClose={closePopup}
        title={popupConfig.title}
        message={popupConfig.message}
        type={popupConfig.type}
        display={popupConfig.display}
      />
    </>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 'calc(100vh - 76px)',
    backgroundColor: '#f8fafc',
    padding: '20px',
    position: 'relative'
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    padding: '32px',
    width: '100%',
    maxWidth: '400px',
    position: 'relative'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
    textAlign: 'center'
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '24px',
    textAlign: 'center'
  },
  error: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '14px',
    color: '#475569',
    fontWeight: '500'
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: '#f8fafc',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  button: {
    backgroundColor: '#2563eb',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'background-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  buttonDisabled: {
    backgroundColor: '#93c5fd',
    cursor: 'not-allowed'
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    margin: '24px 0',
    color: '#cbd5e0',
  },
  dividerText: {
    flex: 1,
    position: 'relative',
    fontSize: '14px',
    color: '#718096',
  },
  socialLogin: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px'
  },
  footer: {
    textAlign: 'center',
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '8px'
  },
  forgotPassword: {
    textAlign: 'center',
    fontSize: '13px',
  },
  forgotLink: {
    color: '#64748b',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s',
  },
  link: {
    color: '#2563eb',
    fontWeight: '500',
    textDecoration: 'none',
    cursor: 'pointer'
  },
  // Full screen loading overlay (for Google)
  fullScreenLoadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999,
  },
  loadingCardFull: {
    textAlign: 'center',
    padding: '40px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
    minWidth: '300px',
  },
  loadingTextFull: {
    marginTop: '20px',
    fontSize: '18px',
    color: '#1e293b',
    fontWeight: '600',
  },
  loadingSubtext: {
    marginTop: '8px',
    fontSize: '14px',
    color: '#64748b',
  },
  // Inline loading overlay (for manual login)
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
    zIndex: 10
  },
  loadingCardInline: {
    textAlign: 'center',
    padding: '20px'
  },
  loadingTextInline: {
    marginTop: '16px',
    fontSize: '16px',
    color: '#475569',
    fontWeight: '500'
  }
};

export default Login;